import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";
import { acct, fmtDate, kes, today } from "@/lib/money";
import { Page, Btn, Tabs, Table, Tr, Td, Field, Loading } from "@/components/kit";
import { download } from "@/lib/download";

const input = "min-h-[40px] rounded-md border border-slate-300 px-2 text-sm text-slate-800";

function Line({ row, indent = true }) {
  return (
    <div className={`flex justify-between gap-4 py-1.5 ${indent ? "pl-4" : ""}`}>
      <Link to={`/ledger/${row.account_id}`} className="hover:text-brand hover:underline">{row.name}</Link>
      <span className="font-mono tabular-nums">{acct(row.balance)}</span>
    </div>
  );
}
function Total({ label, value, strong }) {
  return (
    <div className={`flex justify-between gap-4 py-2 ${strong ? "mt-1 border-t-2 border-[#1C1B1A] text-base font-bold" : "border-t border-[#E4E2DE] font-semibold"}`}>
      <span>{label}</span><span className="font-mono tabular-nums">{acct(value)}</span>
    </div>
  );
}
const Head = ({ children }) => <div className="pt-4 pb-1 font-semibold">{children}</div>;
const card = "mt-4 max-w-[760px] rounded-[10px] border border-[#E4E2DE] bg-white px-6 py-4 text-sm";

export default function Reports() {
  const { company } = useCompany();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "tb";
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(today());
  const branch = params.get("branch") || "";
  const branches = company?.stations || [];
  const [result, setResult] = useState(null);
  useEffect(() => { if (company && !from) setFrom(company.books_start); }, [company, from]);
  useEffect(() => {
    if (!company || !from) return;
    setResult(null);
    const base = `/companies/${company.id}/reports`;
    const req = tab === "pl" ? api.get(`${base}/profit-loss`, { params: { date_from: from, date_to: to, branch: branch || undefined } })
      : api.get(`${base}/${tab === "bs" ? "balance-sheet" : "trial-balance"}`, { params: { as_of: to } });
    req.then(({ data: d }) => setResult({ tab, d }));
  }, [company, tab, from, to, branch]);
  return (
    <Page title="Reports" actions={company && <>
      <Btn onClick={() => {
        const base = `/companies/${company.id}/exports`;
        if (tab === "pl") download(`${base}/profit-loss`, { date_from: from, date_to: to, branch: branch || undefined });
        else download(`${base}/${tab === "bs" ? "balance-sheet" : "trial-balance"}`, { as_of: to });
      }}>Download Excel</Btn>
      <Btn onClick={() => download(`/companies/${company.id}/exports/general-ledger`, { date_from: from || undefined, date_to: to })}>General ledger (all accounts)</Btn>
    </>}>
      <Tabs value={tab} onChange={(v) => setParams(branch ? { tab: v, branch } : { tab: v })} tabs={[{ value: "tb", label: "Trial balance" }, { value: "pl", label: "Profit & loss" }, { value: "bs", label: "Balance sheet" }]} />
      <div className="mt-4 flex flex-wrap gap-3">
        {tab === "pl" && <Field label="From"><input type="date" className={input} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>}
        <Field label={tab === "pl" ? "To" : "As at"}><input type="date" className={input} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        {tab === "pl" && branches.length > 1 && (
          <Field label="Branch">
            <select className={input} value={branch} onChange={(e) => setParams(e.target.value ? { tab, branch: e.target.value } : { tab })}>
              <option value="">All branches</option>
              {branches.map((b) => <option key={b} value={b}>{b}</option>)}
              <option value="head-office">Head office (not tied to a branch)</option>
            </select>
          </Field>
        )}
      </div>
      {result?.tab !== tab ? <Loading /> : tab === "tb" ? <TrialBalance d={result.d} company={company} /> : tab === "pl" ? <ProfitLoss d={result.d} company={company} /> : <BalanceSheet d={result.d} company={company} />}
    </Page>
  );
}

function TrialBalance({ d, company }) {
  return (
    <>
      <div className="mt-4 text-sm text-[#5F5C58]">{company.name} · as at {fmtDate(d.as_of)}</div>
      <Table minWidth={620} cols={[{ label: "Account" }, { label: "Debit", align: "right" }, { label: "Credit", align: "right" }]}
        foot={<tr className="border-t-2 border-[#1C1B1A]"><td className="px-2 py-2.5">Total</td><td className="px-2 py-2.5 text-right tabular-nums">{kes(d.total_debit)}</td><td className="px-2 py-2.5 text-right tabular-nums">{kes(d.total_credit)}</td></tr>}>
        {d.rows.map((r) => (
          <Tr key={r.account_id}>
            <Td><Link to={`/ledger/${r.account_id}`} className="hover:text-brand hover:underline">{r.code} {r.name}</Link></Td>
            <Td right>{r.debit ? kes(r.debit) : ""}</Td><Td right>{r.credit ? kes(r.credit) : ""}</Td>
          </Tr>
        ))}
      </Table>
      {Math.abs(d.total_debit - d.total_credit) >= 0.01 && <p className="mt-2 text-sm font-semibold text-[#A11D1D]">Out of balance by {kes(Math.abs(d.total_debit - d.total_credit))}</p>}
    </>
  );
}

function ProfitLoss({ d, company }) {
  const s = d.sections;
  return (
    <section className={card}>
      <div className="text-[#5F5C58]">{company.name}{d.branch ? ` · ${d.branch === "head-office" ? "Head office" : d.branch}` : ""} · {fmtDate(d.date_from)} – {fmtDate(d.date_to)}</div>
      <Head>Income</Head>{s.sales.map((r) => <Line key={r.account_id} row={r} />)}
      <Total label="Total income" value={d.revenue} />
      <Head>Cost of sales</Head>{s.cost_of_sales.map((r) => <Line key={r.account_id} row={r} />)}
      <Total label={`Gross profit${d.revenue ? ` (${d.gross_margin_pct}%)` : ""}`} value={d.gross_profit} />
      <Head>Expenses</Head>{s.operating.map((r) => <Line key={r.account_id} row={r} />)}
      {s.operating.length === 0 && <div className="py-1.5 pl-4 text-[#5F5C58]">None yet</div>}
      <Total label="Operating profit" value={d.operating_profit} />
      {(s.other_income.length > 0 || s.other_expense.length > 0) && (<><Head>Other</Head>{s.other_income.map((r) => <Line key={r.account_id} row={r} />)}{s.other_expense.map((r) => <Line key={r.account_id} row={{ ...r, balance: -r.balance }} />)}</>)}
      <Total label={d.net_profit < 0 ? "Net loss" : "Net profit"} value={d.net_profit} strong />
    </section>
  );
}

function BalanceSheet({ d, company }) {
  const g = d.groups;
  return (
    <section className={card}>
      <div className="text-[#5F5C58]">{company.name} · as at {fmtDate(d.as_of)}</div>
      <div className="pt-4 text-xs font-bold text-[#5F5C58]">ASSETS</div>
      {g.non_current_assets.length > 0 && <><Head>Non-current assets</Head>{g.non_current_assets.map((r) => <Line key={r.account_id} row={r} />)}<Total label="Total non-current assets" value={d.totals.non_current_assets} /></>}
      <Head>Current assets</Head>{g.current_assets.map((r) => <Line key={r.account_id} row={r} />)}
      <Total label="Total current assets" value={d.totals.current_assets} />
      <Total label="Total assets" value={d.total_assets} strong />
      <div className="pt-5 text-xs font-bold text-[#5F5C58]">LIABILITIES</div>
      {g.current_liabilities.map((r) => <Line key={r.account_id} row={r} />)}
      {g.long_term_liabilities.map((r) => <Line key={r.account_id} row={r} />)}
      <Total label="Total liabilities" value={d.total_liabilities} />
      <div className="pt-5 text-xs font-bold text-[#5F5C58]">EQUITY</div>
      {g.equity.map((r) => <Line key={r.account_id} row={r} />)}
      {d.retained_earnings !== 0 && <div className="flex justify-between py-1.5 pl-4"><span>Profit of earlier years</span><span className="font-mono tabular-nums">{acct(d.retained_earnings)}</span></div>}
      <div className="flex justify-between py-1.5 pl-4"><span>{d.profit_this_year < 0 ? "Loss" : "Profit"} for the year (from {fmtDate(d.financial_year_from)})</span><span className="font-mono tabular-nums">{acct(d.profit_this_year)}</span></div>
      <Total label="Total equity" value={d.total_equity} />
      <Total label="Total liabilities & equity" value={Math.round((d.total_liabilities + d.total_equity) * 100) / 100} strong />
      {!d.balanced && <p className="mt-2 font-semibold text-[#A11D1D]">The balance sheet doesn't balance - check the trial balance.</p>}
    </section>
  );
}
